import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Wallet, ShieldCheck, CheckCircle2, RefreshCw, Loader2, AlertTriangle, Phone, Mail, Globe, Banknote, TrendingUp, IndianRupee, Clock } from "lucide-react";

const KYC_LABELS = {
  created:              { tone: "bg-sun/40 text-ink",       label: "KYC pending — submit docs at razorpay.com" },
  needs_clarification:  { tone: "bg-sun/40 text-ink",       label: "Razorpay needs more info" },
  under_review:         { tone: "bg-sky/15 text-sky",       label: "Under Razorpay review" },
  activated:            { tone: "bg-leaf/30 text-ink",      label: "Activated — auto-payouts live" },
  rejected:             { tone: "bg-danger/15 text-danger", label: "Rejected — please contact support" },
  suspended:            { tone: "bg-danger/15 text-danger", label: "Suspended" },
};

export default function PayoutSetup() {
  const { user } = useAuth();
  const [acc, setAcc] = useState(null);
  const [recent, setRecent] = useState(null);
  const [form, setForm] = useState({
    country: "IN",
    legal_name: "",
    beneficiary_name: "",
    pan: "",
    phone: "",
    bank_account_number: "",
    bank_ifsc: "",
    upi_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/partner/payout-account`);
      if (r.data) {
        setAcc(r.data);
        setForm(f => ({ ...f, ...r.data }));
      }
    } catch { /* first-time vendors have no record yet */ }
    try {
      const r2 = await axios.get(`${API}/partner/payouts/recent`, { params: { days: 30 } });
      setRecent(r2.data);
    } catch { /* ledger empty for new vendors */ }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setErr(""); setSaving(true);
    try {
      const r = await axios.post(`${API}/partner/payout-account`, form);
      setMsg(r.data?.razorpay_linked_account_id ? "Saved & Razorpay Route linked account created." : "Saved.");
      if (r.data?.razorpay_kyc_error) setErr("Razorpay returned: " + r.data.razorpay_kyc_error);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/partner/payout-account/refresh`);
      await load();
    } finally { setRefreshing(false); }
  };

  const kyc = KYC_LABELS[(acc?.razorpay_kyc_status || "").toLowerCase()] || { tone: "bg-line text-ink-soft", label: acc?.razorpay_kyc_status || "Not connected" };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="payout-setup-page">
      <header>
        <h1 className="font-heading text-3xl font-extrabold inline-flex items-center gap-2">
          <Wallet className="w-6 h-6 text-brand"/> Payouts setup
        </h1>
        <p className="text-ink-soft text-sm mt-1">
          Add your bank details so PetBond settles your earnings directly to your account.
          We use <strong>Razorpay Route</strong> (India) and <strong>Stripe Connect</strong> (rest of world) — no money sits with us, payouts are automated at every checkout.
        </p>
      </header>

      {/* Status card */}
      <section className={`rounded-3xl border border-line p-5 ${acc?.razorpay_linked_account_id ? "bg-leaf/10" : "bg-surface"}`} data-testid="payout-status-card">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-2xl bg-brand/15 text-brand grid place-items-center"><ShieldCheck className="w-5 h-5"/></div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-heading text-lg font-extrabold">
              {acc?.razorpay_linked_account_id ? "Razorpay Route — linked" : (acc?.stripe_connected_account_id ? "Stripe Connect — linked" : "Not connected yet")}
            </div>
            {acc?.razorpay_linked_account_id && (
              <div className="text-xs text-ink-soft mt-1 font-mono">{acc.razorpay_linked_account_id}</div>
            )}
            <span className={`inline-block mt-2 text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${kyc.tone}`} data-testid="payout-kyc-status">{kyc.label}</span>
          </div>
          {acc?.razorpay_linked_account_id && (
            <button onClick={refresh} data-testid="payout-refresh-btn" disabled={refreshing} className="text-xs font-bold inline-flex items-center gap-1 rounded-full border border-line px-3 py-2 hover:bg-surface-muted disabled:opacity-60">
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>} Refresh status
            </button>
          )}
        </div>
      </section>

      <RecentPayouts data={recent}/>

      {/* Form */}
      <form onSubmit={submit} className="bg-surface border border-line rounded-3xl p-5 space-y-4" data-testid="payout-form">
        <div className="flex flex-wrap gap-3 items-center">
          <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Country</span>
          <div className="inline-flex rounded-full border border-line bg-surface p-1">
            {[{ id: "IN", label: "India — Razorpay" }, { id: "OTHER", label: "Outside India — Stripe" }].map(opt => (
              <button type="button" key={opt.id} data-testid={`payout-country-${opt.id}`} onClick={() => set("country", opt.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold ${form.country === opt.id ? "bg-brand text-white" : "text-ink-soft"}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {form.country === "IN" ? (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Legal / business name *"><input required value={form.legal_name || ""} onChange={e => set("legal_name", e.target.value)} className="inp" data-testid="payout-legal-name"/></Field>
              <Field label="Beneficiary name (on bank)"><input value={form.beneficiary_name || ""} onChange={e => set("beneficiary_name", e.target.value)} className="inp" data-testid="payout-beneficiary"/></Field>
              <Field label="PAN"><input value={form.pan || ""} onChange={e => set("pan", e.target.value.toUpperCase())} className="inp" maxLength={10} placeholder="ABCDE1234F" data-testid="payout-pan"/></Field>
              <Field label="Phone (Razorpay contact)"><input value={form.phone || ""} onChange={e => set("phone", e.target.value)} className="inp" placeholder="+91-…" data-testid="payout-phone"/></Field>
            </div>
            <div className="border-t border-line pt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-2 inline-flex items-center gap-1"><Banknote className="w-3 h-3 text-brand"/> Bank or UPI (at least one)</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Account number"><input value={form.bank_account_number || ""} onChange={e => set("bank_account_number", e.target.value)} className="inp" data-testid="payout-account-no"/></Field>
                <Field label="IFSC"><input value={form.bank_ifsc || ""} onChange={e => set("bank_ifsc", e.target.value.toUpperCase())} className="inp" maxLength={11} placeholder="HDFC0000001" data-testid="payout-ifsc"/></Field>
                <Field label="UPI ID (alternative)"><input value={form.upi_id || ""} onChange={e => set("upi_id", e.target.value)} className="inp" placeholder="name@upi" data-testid="payout-upi"/></Field>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-surface-muted rounded-2xl p-4 text-sm space-y-2">
            <div className="font-bold inline-flex items-center gap-2"><Globe className="w-4 h-4 text-brand"/> Stripe Connect</div>
            <p className="text-ink-soft">For vendors outside India we use Stripe Connect. Save this form, then complete the Stripe Connect onboarding link emailed to you within 5 minutes. Stripe holds all your bank details — PetBond only sees the account ID.</p>
            <Field label="Existing Stripe Connect account ID (if any)">
              <input value={form.stripe_connected_account_id || ""} onChange={e => set("stripe_connected_account_id", e.target.value)} className="inp" placeholder="acct_…" data-testid="payout-stripe-acct"/>
            </Field>
          </div>
        )}

        {msg && <div className="bg-leaf/30 border border-leaf rounded-2xl px-3 py-2 text-sm font-bold inline-flex items-center gap-2" data-testid="payout-success"><CheckCircle2 className="w-4 h-4 text-leaf"/>{msg}</div>}
        {err && <div className="bg-danger/10 border border-danger/30 rounded-2xl px-3 py-2 text-sm font-bold text-danger inline-flex items-center gap-2" data-testid="payout-error"><AlertTriangle className="w-4 h-4"/>{err}</div>}

        <button disabled={saving} data-testid="payout-save-btn" className="bg-brand text-white rounded-full px-5 py-3 font-bold inline-flex items-center gap-2 hover:bg-brand-hover disabled:opacity-60">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin"/> Saving…</> : "Save & connect"}
        </button>
        <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.inp:focus{border-color:#FF6B35;}`}</style>
      </form>

      <div className="bg-ink/5 rounded-3xl p-5 text-sm space-y-1.5">
        <div className="font-heading font-extrabold inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand"/> How payouts work</div>
        <ul className="list-disc pl-5 space-y-1 text-ink-soft">
          <li>Customer pays for a booking/order → Razorpay routes <strong>your share to your bank automatically</strong> and the platform fee to PetBond at the same instant.</li>
          <li>Settlement to your bank usually happens in <strong>T+2 business days</strong>, controlled by Razorpay&apos;s standard settlement schedule.</li>
          <li>Refunds reverse the linked-account portion automatically — no chasing PetBond ops.</li>
          <li>Your bank details are stored encrypted with Razorpay, never on PetBond servers.</li>
        </ul>
      </div>
    </div>
  );
}

function RecentPayouts({ data }) {
  if (!data) return null;
  const totals = data.totals || { earned: 0, paid_out: 0, pending: 0, entry_count: 0 };
  const ledger = data.ledger_entries || [];
  const transfers = data.razorpay_transfers || [];
  const empty = ledger.length === 0 && transfers.length === 0;
  return (
    <section className="space-y-3" data-testid="recent-payouts">
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="font-heading text-xl font-bold inline-flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-brand"/> Last {data.window_days} days · payouts
        </h2>
        {transfers.length > 0 && (
          <span className="text-[11px] font-bold text-leaf inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> Live from Razorpay Route</span>
        )}
      </header>

      <div className="grid grid-cols-3 gap-3" data-testid="recent-payouts-totals">
        <Tile label="Earned" value={`₹${totals.earned.toLocaleString('en-IN')}`} sub={`${totals.entry_count} transactions`} tone="bg-brand/10 text-brand"/>
        <Tile label="Paid out" value={`₹${totals.paid_out.toLocaleString('en-IN')}`} sub="Settled to bank" tone="bg-leaf/30 text-ink"/>
        <Tile label="Pending" value={`₹${totals.pending.toLocaleString('en-IN')}`} sub="In settlement queue" tone="bg-sun/40 text-ink"/>
      </div>

      {empty ? (
        <div className="bg-surface-muted border border-line rounded-2xl p-5 text-center text-sm text-ink-soft" data-testid="recent-payouts-empty">
          No payouts in the last {data.window_days} days yet. Once a customer pays for a booking, your share will appear here in real time.
        </div>
      ) : (
        <div className="bg-surface border border-line rounded-3xl overflow-hidden">
          {/* Razorpay transfers shown on top — they're the real money movement */}
          {transfers.map(t => (
            <div key={t.transfer_id} className="px-4 py-3 border-b border-line flex items-center gap-3" data-testid={`recent-transfer-${t.transfer_id}`}>
              <div className="w-9 h-9 rounded-xl bg-leaf/30 text-ink grid place-items-center"><IndianRupee className="w-4 h-4"/></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">₹{t.amount.toLocaleString('en-IN')} <span className="text-xs text-ink-soft font-normal">net to bank</span></div>
                <div className="text-[11px] text-ink-soft">{t.created_at ? new Date(t.created_at).toLocaleString('en-IN') : ""} · Razorpay #{t.transfer_id?.slice(-8)}</div>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1 ${t.status === 'processed' ? 'bg-leaf/30 text-ink' : 'bg-sun/40 text-ink'}`}>{t.status}</span>
            </div>
          ))}
          {ledger.slice(0, 20).map(e => (
            <div key={e.earning_id || `${e.vendor_user_id}-${e.created_at}`} className="px-4 py-3 border-b border-line last:border-b-0 flex items-center gap-3" data-testid={`recent-ledger-${e.earning_id || 'x'}`}>
              <div className="w-9 h-9 rounded-xl bg-brand/10 text-brand grid place-items-center"><Wallet className="w-4 h-4"/></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">₹{(e.vendor_amount || 0).toLocaleString('en-IN')} <span className="text-xs text-ink-soft font-normal">· {e.category || e.source_type || "booking"}</span></div>
                <div className="text-[11px] text-ink-soft">
                  {e.created_at ? new Date(e.created_at).toLocaleString('en-IN') : ""}
                  {e.gross_amount ? ` · gross ₹${e.gross_amount} · commission ₹${(e.gross_amount - e.vendor_amount).toFixed(0)}` : ""}
                </div>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1 ${e.status === 'paid' ? 'bg-leaf/30 text-ink' : 'bg-sun/40 text-ink'}`}>{e.status || "pending"}</span>
            </div>
          ))}
          {ledger.length > 20 && (
            <div className="px-4 py-3 text-xs text-ink-soft text-center font-bold">+ {ledger.length - 20} more transactions</div>
          )}
        </div>
      )}
    </section>
  );
}

function Tile({ label, value, sub, tone }) {
  return (
    <div className={`rounded-2xl p-3 ${tone}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-heading text-xl md:text-2xl font-extrabold mt-0.5">{value}</div>
      <div className="text-[10px] opacity-80 mt-0.5">{sub}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
